import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { BulkResultItem } from '@nucleus-commerce/api-client/admin';

import { scheduleItemCopy } from '../api/schedule-errors';
import { useScheduleName } from '../hooks/use-schedule';
import { useScheduleCopy } from '../hooks/use-schedule-copy';

export interface MemberResultDetailProps {
  item: Pick<BulkResultItem, 'code' | 'meta'>;
  /** The schedule's target stores, to name `meta.storeId`. */
  storeNames?: ReadonlyMap<string, string>;
  /** Following the owner link closes whatever shows this row. */
  onNavigate?: () => void;
}

/**
 * Why one product was refused, beyond the generic code line: the store and the handle that
 * clash (`ALREADY_EXISTS`), or the schedule that already owns it there (`SCHEDULE_CONFLICT`).
 */
export function MemberResultDetail({ item, storeNames, onNavigate }: MemberResultDetailProps) {
  const { t } = useTranslation('schedules');
  const copy = scheduleItemCopy(item);
  const text = useScheduleCopy(storeNames);
  const ownerId = copy?.conflict?.scheduleId ?? null;
  const owner = useScheduleName(ownerId);
  if (!copy) return null;
  return (
    <span className="text-muted-foreground flex flex-col gap-0.5 text-xs">
      <span>{text(copy)}</span>
      {ownerId ? (
        <span>
          {t('errors.ownedBy')}{' '}
          <Link
            to="/org/schedules/$scheduleId"
            params={{ scheduleId: ownerId }}
            className="text-foreground font-medium underline underline-offset-2"
            onClick={onNavigate}
          >
            {owner.data ?? t('errors.otherSchedule')}
          </Link>
        </span>
      ) : null}
    </span>
  );
}
